import cn from "classnames";
import routes from "../config/routes";
import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLogout } from "../hooks/user";
import useAuth from "../hooks/use-auth";
import { makeSidebarMenu } from "src/lib/utils";
import Tooltip from "@mui/material/Tooltip";

import GlobalContext from "src/context/global-context";
import CustomTooltip from "src/components/dashboard/custom-tooltip";

const isPathActive = (targetHref?: string, currentPath?: string): boolean => {
  if (!targetHref || !currentPath) return false;
  if (currentPath === targetHref) return true;
  if (targetHref !== "/" && currentPath.startsWith(`${targetHref}/`)) {
    return true;
  }
  return false;
};

const isItemActive = (item: any, currentPath: string): boolean => {
  if (!item) return false;
  if (item.link && isPathActive(item.link, currentPath)) {
    return true;
  }
  if (item.subMenu && Array.isArray(item.subMenu)) {
    return item.subMenu.some((sub: any) => isItemActive(sub, currentPath));
  }
  return false;
};

interface NavLinkProps {
  handleToggle: (index: number) => void;
  subMenuOpen: number | null;
  index: number;
  href: string;
  setCollapse?: (val: boolean) => void;
  title: string;
  icon: React.ReactNode;
  arrowUpIcon?: React.ReactNode;
  arrowDownIcon?: React.ReactNode;
  activeIcon: React.ReactNode;
  isCollapse?: boolean;
  subMenu?: any;
}

function NavLink({
  subMenuOpen,
  handleToggle,
  index,
  href,
  setCollapse,
  icon,
  arrowUpIcon,
  arrowDownIcon,
  activeIcon,
  title,
  isCollapse,
  subMenu,
}: NavLinkProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { unauthorize } = useAuth();
  const { mutate: logout } = useLogout();
  const [isMobile, setIsMobile] = useState(false);
  const [arrowStatus, setArrowStatus] = useState(false);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);
    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(false);
    if (window.innerWidth < 640) {
      setIsMobile(true);
    }
  }

  const isCurrentActive = isItemActive({ link: href, subMenu }, location.pathname);
  const isOpen = Boolean(subMenu && subMenuOpen === index);

  const onClick = (subHref: string) => {
    isMobile && setCollapse && setCollapse(false);
    if (href === routes.logout) {
      unauthorize();
      logout();
      navigate(routes.home);
    } else {
      navigate(subHref ? subHref : href);
    }
  };

  const onRowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (subMenu) {
      handleToggle(index);
    } else {
      onClick("");
    }
  };

  return (
    <div key={index} className="">
      <a
        onClick={onRowClick}
        className={cn(
          isCurrentActive || isOpen ? "bg-active" : "",
          "my-0.5 flex justify-between gap-1 px-4 py-4 rounded-[10px] md:pl-7 md:px-3 xs:px-6 sm:my-1 sm:gap-1.5 sm:px-7 lg:gap-2 xl:my-0.5 w-[calc(100%-20px)] mx-[10px] cursor-pointer"
        )}
      >
        <span className="flex justify-between">
          <span
            className={cn(
              "flex flex-shrink-0 items-center justify-start cursor-pointer",
              isCollapse
                ? "w-[20px] xl:w-auto"
                : "w-auto xl:w-[20px] -translate-x-[10px]"
            )}
          >
            {isCurrentActive || isOpen ? activeIcon : icon}
          </span>
          <span
            className={cn(
              "font-manrope font-medium text-[14px] dark:text-light-400 transition-all ease-out duration-300",
              isCollapse
                ? "inline-flex xl:opacity-0 opacity-100"
                : "opacity-0 xl:inline-flex xl:opacity-100",
              isCurrentActive || isOpen
                ? "text-primary"
                : "text-secondary"
            )}
          >
            {title}
          </span>
        </span>
        {subMenu && (
          <span
            className={cn(
              "flex items-center cursor-pointer",
              isCollapse
                ? "inline-flex xl:opacity-0 xl:pointer-events-none"
                : "opacity-0 xl:opacity-100 xl:inline-flex"
            )}
          >
            {isOpen ? arrowUpIcon : arrowDownIcon}
          </span>
        )}
      </a>
      {subMenu && (
        <ul
          className={cn(
            isOpen ? "flex" : "hidden",
            "my-0.5 flex-col gap-1 px-4 py-4 rounded-[10px] xs:px-6 sm:my-1 sm:gap-1.5 sm:px-7 lg:gap-2 xl:my-0.5 cursor-pointer w-[calc(100%-20px)] mx-[10px]"
          )}
        >
          {subMenu.map((subMenuitem: any, subIndex: number) => {
            const active = isPathActive(subMenuitem.link, location.pathname);
            return (
              <li key={`submenu_${subIndex}`}>
                <a
                  className={cn(
                    active
                      ? "text-primary font-semibold"
                      : "text-secondary hover:text-primary",
                    "cursor-pointer"
                  )}
                  onClick={() => onClick(subMenuitem.link)}
                >
                  {subMenuitem.title}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function Sidebar({
  isCollapse,
  setCollapse,
  className = "sm:flex fixed bottom-0 z-20 pt-16",
}: {
  isCollapse?: boolean;
  setCollapse?: any;
  className?: string;
}) {
  const location = useLocation();
  const { getCurrrentUser } = useAuth();
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  const currentUser = getCurrrentUser();
  const userPermissions =
    currentUser?.permissions || currentUser?.role?.permissions || [];
  const permissions = useMemo(
    () => makeSidebarMenu(userPermissions),
    [userPermissions]
  );

  const [subMenuOpen, setSubMenuOpen] = useState<number | null>(() => {
    const activeIdx = permissions.findIndex(
      (item) => item?.subMenu && isItemActive(item, location.pathname)
    );
    return activeIdx !== -1 ? activeIdx : null;
  });

  const lastPathRef = useRef<string>(location.pathname);
  const isInitializedRef = useRef(false);

  // Initialize once if permissions were empty on initial mount
  useEffect(() => {
    if (!isInitializedRef.current && permissions.length > 0) {
      const activeIdx = permissions.findIndex(
        (item) => item?.subMenu && isItemActive(item, location.pathname)
      );
      if (activeIdx !== -1) {
        setSubMenuOpen(activeIdx);
        isInitializedRef.current = true;
      }
    }
  }, [permissions, location.pathname]);

  // Sync subMenuOpen ONLY when the route actually changes (navigation)
  useEffect(() => {
    if (lastPathRef.current !== location.pathname) {
      lastPathRef.current = location.pathname;
      const activeIdx = permissions.findIndex(
        (item) => item?.subMenu && isItemActive(item, location.pathname)
      );
      setSubMenuOpen(activeIdx !== -1 ? activeIdx : null);
    }
  }, [location.pathname, permissions]);

  const handleToggle = (index: number) => {
    setSubMenuOpen((prev) => (prev === index ? null : index));
  };

  return (
    <aside
      className={cn(
        "h-full flex-col justify-between overflow-y-auto border-r border-light-400 bg-light text-dark-900 dark:border-0 dark:bg-dark-200 transition-all duration-300 sm:pt-[70px]",
        isCollapse
          ? "sm:w-60 xl:w-[75px] w-60 left-0"
          : "sm:w-[75px] xl:w-72 w-72 -left-72 sm:left-0",
        className
      )}
    >
      {getCurrrentUser()?.google2fa_enabled === 0 && (
        <div
          className="absolute top-0 left-0 h-full w-full z-[999999] cursor-pointer"
          onClick={() => {
            setAlertText("Please Enable 2FA");
            setIsAlertOpened(true);
          }}
        ></div>
      )}
      <div className="flex h-full w-full flex-col overflow-y-auto scrollbar-hide pt-[60px]">
        <nav className="flex flex-col mt-2 mb-20">
          {permissions &&
            permissions.map((item, index) => {
              return (
                <React.Fragment key={`navbar_frag_${index}`}>
                  {item && (
                    <NavLink
                      handleToggle={handleToggle}
                      subMenuOpen={subMenuOpen}
                      index={index}
                      key={`navbar_${index}`}
                      title={item.title}
                      setCollapse={setCollapse}
                      href={item.link}
                      isCollapse={isCollapse}
                      icon={item.icon}
                      arrowUpIcon={item.arrowUpIcon}
                      arrowDownIcon={item.arrowDownIcon}
                      activeIcon={item.activeIcon}
                      subMenu={item.subMenu}
                    />
                  )}
                </React.Fragment>
              );
            })}
        </nav>
      </div>
    </aside>
  );
}


